export interface ObjectStoreDefinition {
  name: string;
  options: IDBObjectStoreParameters;
  indexes: ObjectStoreIndexDefinition[];
}

export interface ObjectStoreIndexDefinition {
  name: string;
  keyPath: string | Iterable<string>;
  options: IDBIndexParameters;
}

export interface StoreTransaction {
  store: IDBObjectStore;
  transaction: IDBTransaction;
  database: IDBDatabase;
}

export interface QueryOptions {
  sort?: IDBCursorDirection;
  sortIndex?: string;
  limit?: number;
  predicate?: (value: any) => boolean;
}

export class IndexedDBRepository<T, NewT = Omit<T, 'id'>> {
  public readonly name: string;
  public readonly version: number;
  public readonly objectStoreDefinition: ObjectStoreDefinition;
  protected database?: IDBDatabase;

  constructor(name: string, version: number, objectStore: ObjectStoreDefinition) {
    this.name = name;
    this.version = version;
    this.objectStoreDefinition = {
      ...objectStore,
      options: { keyPath: 'id', autoIncrement: true, ...objectStore.options },
    };
  }

  protected async openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.name, this.version);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onupgradeneeded = (event) => {
        this.handleUpgradeNeeded(event);
      };
    });
  }

  protected handleUpgradeNeeded(event: IDBVersionChangeEvent): void {
    const database = (event.target as IDBOpenDBRequest).result;
    if (!database.objectStoreNames.contains(this.objectStoreDefinition.name)) {
      const objectStore = database.createObjectStore(
        this.objectStoreDefinition.name,
        this.objectStoreDefinition.options
      );
      this.objectStoreDefinition.indexes.forEach((index: ObjectStoreIndexDefinition) => {
        objectStore.createIndex(index.name, index.keyPath, index.options);
      });
    } else {
      // delete all indexes and recreate them
      const transaction = (event.target as IDBOpenDBRequest).transaction;
      if (transaction) {
        const objectStore = transaction.objectStore(this.objectStoreDefinition.name);
        const existingIndexes = new Set(objectStore.indexNames as unknown as string[]);
        existingIndexes.forEach((indexName) => {
          if (indexName !== 'id') {
            objectStore.deleteIndex(indexName);
          }
        });
        this.objectStoreDefinition.indexes.forEach((index) => {
          objectStore.createIndex(index.name, index.keyPath, index.options);
        });
      }
    }
  }

  protected async newStoreTransaction(mode: IDBTransactionMode): Promise<StoreTransaction> {
    if (!this.database) {
      this.database = await this.openDatabase();
    }
    const transaction = this.database.transaction([this.objectStoreDefinition.name], mode);
    const store = transaction.objectStore(this.objectStoreDefinition.name);
    return {
      store,
      transaction,
      database: this.database,
    };
  }

  /**
   * Close the database connection
   * returns immediately and closes the connection in a separate thread.
   */
  public closeDatabase(): void {
    if (this.database) {
      this.database.close();
      this.database = undefined;
    }
  }

  /**
   * Adds a new record to the object store
   * @param value Optional: Include keyPath key:value in the value object (default is auto-increment)
   */
  public async add(value: NewT): Promise<IDBValidKey> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const request = store.add(value);
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Updates a record in the object store
   * Will "add" if keyPath key:value is not found or undefined
   * @param value Include keyPath key:value in the value object
   */
  public async update(value: T): Promise<IDBValidKey> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const request = store.put(value);
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Deletes a record from the object store
   * @param key
   */
  public async delete(key: IDBValidKey): Promise<IDBValidKey> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const request = store.delete(key);
      transaction.oncomplete = () => resolve(key);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Deletes multiple records from the object store
   * @param predicate A function that returns true for records that should be deleted
   */
  public async deleteWhere(predicate: (value: T) => boolean): Promise<IDBValidKey[]> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const request = store.openCursor();
      const results: IDBValidKey[] = [];
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          if (predicate(cursor.value as T)) {
            const request = store.delete(cursor.primaryKey);
            request.onsuccess = () => results.push(cursor.primaryKey);
          }
          cursor.continue();
        }
      };
      transaction.oncomplete = () => resolve(results);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Clears all records from the object store
   */
  public async clear(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const request = store.clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieves a single record from the object store by its key
   * @param key
   */
  public async get(key: IDBValidKey): Promise<T> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readonly');
      const request = store.get(key);
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieves all records from the object store
   */
  public async getAll(options: QueryOptions = {}): Promise<T[]> {
    return new Promise(async (resolve, reject) => {
      const { sort = 'next', sortIndex, limit, predicate = () => true } = options;

      const { store, transaction } = await this.newStoreTransaction('readonly');

      if (sortIndex && !store.indexNames.contains(sortIndex)) {
        reject(new Error(`Index not found for sortIndex`));
        return;
      }
      if (limit !== undefined && limit <= 0) {
        reject(new Error(`Limit must be greater than 0`));
        return;
      }

      let counter = limit || Infinity;

      const request = (sortIndex ? store.index(sortIndex) : store).openCursor(null, sort);

      const results: T[] = [];

      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          if (predicate(cursor.value as T)) {
            results.push(cursor.value);
            counter -= 1;
            if (counter <= 0) return;
          }
          cursor.continue();
        }
      };
      transaction.oncomplete = () => resolve(results);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieves all records from the object store using a specific index
   * @param indexName
   * @param key
   */
  public async getByIndex(indexName: string, key: IDBValidKey): Promise<T[]> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readonly');
      if (!store.indexNames.contains(indexName)) {
        reject(new Error(`Index not found`));
        return;
      }
      const request = store.index(indexName).openCursor(key);
      const results: T[] = [];
      request.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        }
      };
      transaction.oncomplete = () => resolve(results);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Retrieves all records from the object store
   * @param predicate A function that returns true for records that should be included in the result
   * @param options
   */
  public async getWhere(
    predicate: (value: T) => boolean,
    options: QueryOptions = {}
  ): Promise<T[]> {
    return this.getAll({ ...options, predicate });
  }

  public async count(): Promise<number> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readonly');
      const request = store.count();
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(request.error);
    });
  }

  /**
   * Adds multiple records to the object store
   * @param values Include keyPath key:value in the value to explicitly set the key (default is auto-increment)
   */
  public async addBulk(values: NewT[]): Promise<IDBValidKey[]> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const results: IDBValidKey[] = [];
      let error: IDBRequest['error'];

      transaction.oncomplete = () => resolve(results);
      transaction.onerror = () => reject(error);

      values.forEach((value) => {
        const request = store.add(value);
        request.onsuccess = () => results.push(request.result);
        request.onerror = () => {
          error = request.error;
        };
      });
    });
  }

  /**
   * Updates multiple records in the object store
   * Will "add" if keyPath key:value is not found or undefined
   * @param values Include keyPath key:value in the value
   */
  public async updateBulk(values: T[]): Promise<IDBValidKey[]> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const results: IDBValidKey[] = [];
      let error: IDBRequest['error'];

      transaction.oncomplete = () => resolve(results);
      transaction.onerror = () => reject(error);

      values.forEach((value) => {
        const request = store.put(value);
        request.onsuccess = () => results.push(request.result);
        request.onerror = () => {
          error = request.error;
        };
      });
    });
  }

  /**
   * Deletes multiple records from the object store
   * @param keys
   */
  public async deleteBulk(keys: IDBValidKey[]): Promise<IDBValidKey[]> {
    return new Promise(async (resolve, reject) => {
      const { store, transaction } = await this.newStoreTransaction('readwrite');
      const results: IDBValidKey[] = [];
      let error: IDBRequest['error'];

      transaction.oncomplete = () => resolve(results);
      transaction.onerror = () => reject(error);

      keys.forEach((key) => {
        const request = store.delete(key);
        request.onsuccess = () => results.push(key);
        request.onerror = () => {
          error = request.error;
        };
      });
    });
  }
}
